import { useCallback, useEffect, useReducer, useRef } from 'react';
import { auth, storage } from '../firebase';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { scanPhotos } from '../photoUpload/exifScan';
import { generateThumbnail } from '../photoUpload/thumbnails';
import UploadQueue from '../upload/uploadQueue';
import { photoStoragePath, photoThumbPath } from '../photoUpload/photoKeys';
import { diffAgainstExisting } from '../upload/dedup';
import { evaluateCaps } from '../photoUpload/caps';
import { beginBatch, completeBatch } from '../photoUpload/batchRecord';
import { trackEvent } from '../analytics';
import { photoCountBucket } from '../analytics-helpers';

const BYTES_PER_MB = 1024 * 1024;
const toMb = (bytes) => Math.round((bytes || 0) / BYTES_PER_MB);

// The thumbnail is a non-fatal, best-effort follow-on to the original upload.
// Bound the whole step so a stalled thumbnail upload — or a slow/hung canvas
// decode — can't wedge a photo whose original already landed (the failure that
// froze the batch at "1 uploading"). Comfortably under the queue's stall
// watchdog so a hung thumbnail resolves cleanly instead of failing the photo.
const THUMBNAIL_TIMEOUT_MS = 20 * 1000;

// Photo upload lifecycle. All Firebase + queue mechanics live here so the
// UI components stay presentational.
//
// State machine:
//   idle → scanning → preflight → uploading → done
//                    ↓            ↓      ↘
//                  cancelled    error   partial ⇄ uploading (retryFailed)
//
// 'partial' is a terminal-but-recoverable state: the queue finished with
// failedCount > 0, so we do NOT claim success — we surface the shortfall and
// let the user retry just the failed items. Without this the UI reported
// "done" even when transient upload failures silently dropped photos.
//
// After a clean 'done' (every storage upload succeeded client-side) a SERVER
// drop can still leave a photo doc unwritten — the object reached Storage but
// its ingest trigger was dropped (issue #361). The client can't see that from
// the upload queue (which only knows the byte upload succeeded), so once the
// batch settles we reconcile the uploaded keys against the photo docs that
// actually landed and, if any are missing, call reconcile_project_photos to
// backfill them. `reconcile` carries the outcome for the UI; it never changes
// `phase`, so a clean upload still reads as 'done'.
//
// Every terminal state clears any in-flight timers/subscriptions in cleanup.

const INITIAL_STATE = {
  phase: 'idle',
  scan: null,             // { entries, dayBuckets, skippedNonImages, missingExif, totals }
  dedup: null,            // { toUpload, alreadyUploaded, totals }
  caps: null,             // evaluateCaps() result
  progress: null,         // queue aggregate snapshot
  itemStates: [],         // per-file snapshot
  error: null,
  reconcile: null,        // { status: 'checking'|'clean'|'recovered'|'incomplete', missing, recovered }
};

function reducer(state, action) {
  switch (action.type) {
    case 'scanStart':
      return { ...INITIAL_STATE, phase: 'scanning' };
    case 'scanDone':
      return {
        ...state,
        phase: 'preflight',
        scan: action.scan,
        dedup: action.dedup,
        caps: action.caps,
      };
    case 'capsUpdate':
      return { ...state, caps: action.caps };
    case 'startUploading':
      return { ...state, phase: 'uploading', reconcile: null };
    case 'progress':
      return { ...state, progress: action.aggregate, itemStates: action.items };
    case 'finish':
      return { ...state, phase: action.phase, progress: action.aggregate, itemStates: action.items };
    case 'reconcileStart':
      return { ...state, reconcile: { status: 'checking', missing: 0, recovered: 0 } };
    case 'reconcileDone':
      return { ...state, reconcile: action.reconcile };
    case 'dismissReconcile':
      return { ...state, reconcile: null };
    case 'error':
      return { ...state, phase: 'error', error: action.error };
    case 'reset':
      return INITIAL_STATE;
    default:
      return state;
  }
}

// The default uploader wires the queue up to Firebase Storage + a canvas
// thumbnail generator. Injectable through options so tests can drive the
// hook without hitting Firebase.
export function defaultBuildUploader({ uid, projectId }) {
  return async function uploadItem(item, { onProgress, signal }) {
    const originalPath = photoStoragePath(uid, projectId, item.photoKey);
    const metadata = {
      customMetadata: {
        capturedAt: item.capturedAt != null ? String(item.capturedAt) : '',
        capturedAtSource: item.capturedAtSource || 'fileModified',
        clientKey: item.photoKey,
      },
    };
    if (item.cameraId) metadata.customMetadata.cameraId = item.cameraId;

    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(ref(storage, originalPath), item.file, metadata);
      const abortHandler = () => { try { task.cancel(); } catch (_e) { /* ignore */ } };
      if (signal) {
        if (signal.aborted) abortHandler();
        else signal.addEventListener('abort', abortHandler, { once: true });
      }
      task.on(
        'state_changed',
        (snap) => {
          onProgress({ bytesTransferred: snap.bytesTransferred, totalBytes: snap.totalBytes });
        },
        (err) => {
          if (signal) signal.removeEventListener('abort', abortHandler);
          reject(err);
        },
        () => {
          if (signal) signal.removeEventListener('abort', abortHandler);
          resolve();
        },
      );
    });

    // Thumbnail is pipelined after the original so a bad thumbnail can't fail
    // the photo. Both a thrown error AND a silent hang are non-fatal: the whole
    // step is bounded by THUMBNAIL_TIMEOUT_MS so a stalled thumbnail upload
    // never freezes the item — the original already succeeded and was counted.
    let thumbTask = null;
    let thumbTimeout = null;
    // Set the instant the timeout wins (and again in cleanup). The hang can be
    // in the canvas decode itself, not just the upload; if that decode finally
    // resolves *after* the timeout released the item, it must NOT go on to
    // start an orphaned thumbnail upload that nothing awaits or cancels.
    let abandoned = false;
    try {
      await Promise.race([
        (async () => {
          const thumb = await generateThumbnail(item.file);
          if (!thumb || abandoned) return;
          const thumbRef = ref(storage, photoThumbPath(uid, projectId, item.photoKey));
          thumbTask = uploadBytesResumable(thumbRef, thumb, {
            contentType: 'image/jpeg',
            customMetadata: { clientKey: item.photoKey },
          });
          await new Promise((resolve) => {
            thumbTask.on('state_changed', null, () => resolve(), () => resolve());
          });
        })(),
        new Promise((resolve) => {
          thumbTimeout = setTimeout(() => { abandoned = true; resolve(); }, THUMBNAIL_TIMEOUT_MS);
        }),
      ]);
    } catch (_err) { /* thumbnail failure is non-fatal */ } finally {
      abandoned = true;
      if (thumbTimeout) clearTimeout(thumbTimeout);
      // If we bailed on a timeout with the upload still running, stop it so it
      // doesn't keep a socket open behind the finished photo.
      if (thumbTask) { try { thumbTask.cancel(); } catch (_e) { /* ignore */ } }
    }

    return { storagePath: originalPath };
  };
}

// Fetch existing photo doc IDs for a project. One-shot query — never listen
// to the whole photos collection or the client memory footprint blows up on
// a 10,000-photo project.
export async function fetchExistingPhotoIds(projectId, {
  getDocsFn = getDocs,
  collectionFn = collection,
} = {}) {
  if (!projectId) return new Set();
  const col = collectionFn(db, 'projects', projectId, 'photos');
  const snap = await getDocsFn(col);
  const ids = new Set();
  snap.forEach((docSnap) => ids.add(docSnap.id));
  return ids;
}

// Call the server recovery path for a project. Lists its storage objects,
// backfills a photo doc for any orphan a dropped ingest trigger left behind,
// and recomputes coverage. Returns { scanned, recovered, totalPhotos }.
export async function defaultReconcile(projectId) {
  const fn = httpsCallable(getFunctions(), 'reconcile_project_photos');
  const res = await fn({ projectId });
  return res?.data || {};
}

// Grace before the first landed-count check and between re-checks. A finalized
// object's ingest trigger fires asynchronously, so a freshly-landed doc can lag
// the client by a few seconds — polling avoids escalating to the (heavier)
// storage-listing reconcile for what is really just normal trigger latency.
const VERIFY_DELAY_MS = 2500;
const VERIFY_ATTEMPTS = 3;

export default function usePhotoUpload(projectId, {
  buildUploader = defaultBuildUploader,
  fetchExistingIds = fetchExistingPhotoIds,
  reconcileFn = defaultReconcile,
  runScan = scanPhotos,
  existingPhotos = 0,
  existingBytes = 0,
  // Pooled ACCOUNT storage: total tier allowance + account-wide used bytes
  // (across all projects, all artifact kinds). Since #342 this is the ONLY
  // ceiling — the per-project caps are gone. null total = allowance unknown →
  // pool meter off.
  // poolLoading = the account-wide usage figure hasn't resolved yet; while true
  // evaluateCaps withholds the pooled verdict (fail-closed) instead of metering
  // a batch against the stale 0-used initial state (#362).
  poolTotalBytes = null,
  poolUsedBytes = 0,
  poolLoading = false,
  cameraId = null,
  createQueue = (opts) => new UploadQueue(opts),
  verifyDelayMs = VERIFY_DELAY_MS,
  verifyAttempts = VERIFY_ATTEMPTS,
} = {}) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const queueRef = useRef(null);
  // Enriched entries for the current batch, kept so retryFailed() can rebuild a
  // queue from just the failed items (queue.getItems() drops the File handle).
  const batchItemsRef = useRef([]);
  // Guards the async post-upload reconcile from dispatching after unmount.
  const mountedRef = useRef(true);
  // photo_cap_hit is a per-SCAN signal, not a per-render one. caps is
  // re-verdicted whenever a pooled input changes while a preflight is open, so
  // without this latch one drop could emit the event several times and inflate
  // the cap-hit rate. Cleared on every new scan.
  const capHitFiredRef = useRef(false);

  // Emit photo_cap_hit for a blocked preflight, at most once per scan.
  //
  // Fires only on a REAL over-cap verdict. caps.blocked is also true while the
  // pooled usage rollup is still resolving (poolPending fails closed, #362) —
  // that is a transient "calculating" state, not a cap the user hit, and
  // counting it would swamp the metric with noise from every dialog opened
  // during the load window. When that pending verdict later resolves to a genuine
  // overage the re-verdict effect emits it then, still once for the scan.
  const emitCapHit = useCallback((caps, batch) => {
    if (!caps || capHitFiredRef.current) return;
    // Since #342 there is exactly one blocking dimension: the pooled account
    // allowance. `cap_type` is kept (the #315 dashboards group on it) and now
    // only ever carries the value it already used for pooled overage, so the
    // existing series continues rather than being renamed.
    if (!caps.overPoolCap) return;
    capHitFiredRef.current = true;
    trackEvent('photo_cap_hit', {
      project_id: projectId,
      cap_type: 'account_storage',
      over_pool_cap: true,
      // Raw figures: the distribution of how far ABOVE the pool a blocked batch
      // projects is what tells us whether a tier's allowance is too small.
      existing_photos: existingPhotos,
      batch_photos: batch?.toUploadCount || 0,
      projected_photos: caps.projectedPhotos,
      batch_mb: toMb(batch?.toUploadBytes),
      projected_mb: toMb(caps.projectedBytes),
      pool_used_mb: toMb(caps.poolUsedBytes),
      pool_total_mb: toMb(caps.poolTotalBytes),
      projected_pool_mb: toMb(caps.projectedPoolBytes),
      count_bucket: photoCountBucket(caps.projectedPhotos),
    });
  }, [projectId, existingPhotos]);

  // After a clean batch, verify the photos the client uploaded actually landed
  // as docs and, if not, recover them. Never touches `phase` — a clean upload
  // stays 'done'; this only sets the `reconcile` field the UI surfaces.
  const verifyAndReconcile = useCallback(async (uploadedKeys) => {
    if (!uploadedKeys || uploadedKeys.length === 0) return;
    dispatch({ type: 'reconcileStart' });
    const finish = (reconcile, evt) => {
      if (evt) trackEvent('photo_upload_reconciled', { project_id: projectId, ...evt });
      if (mountedRef.current) dispatch({ type: 'reconcileDone', reconcile });
    };
    try {
      // Poll for the docs to appear — most "missing" keys are just trigger lag.
      let missing = uploadedKeys;
      for (let attempt = 0; attempt < Math.max(1, verifyAttempts); attempt += 1) {
        if (verifyDelayMs > 0) await new Promise((r) => setTimeout(r, verifyDelayMs));
        if (!mountedRef.current) return;
        const landed = await fetchExistingIds(projectId);
        missing = uploadedKeys.filter((k) => !landed.has(k));
        if (missing.length === 0) {
          finish({ status: 'clean', missing: 0, recovered: 0 });
          return;
        }
      }
      // A real gap persisted past the grace window — escalate to the server
      // reconcile, which backfills docs for orphaned storage objects.
      let recovered = 0;
      try {
        const res = await reconcileFn(projectId);
        recovered = res?.recovered || 0;
      } catch (err) {
        finish(
          { status: 'incomplete', missing: missing.length, recovered: 0 },
          { missing: missing.length, recovered: 0, outcome: 'error' },
        );
        return;
      }
      if (!mountedRef.current) return;
      const landedAfter = await fetchExistingIds(projectId);
      const stillMissing = uploadedKeys.filter((k) => !landedAfter.has(k));
      const status = stillMissing.length > 0
        ? 'incomplete'
        : (recovered > 0 ? 'recovered' : 'clean');
      finish(
        { status, missing: stillMissing.length, recovered },
        { missing: missing.length, recovered, still_missing: stillMissing.length, outcome: status },
      );
    } catch (err) {
      finish({ status: 'incomplete', missing: uploadedKeys.length, recovered: 0 });
    }
  }, [projectId, fetchExistingIds, reconcileFn, verifyDelayMs, verifyAttempts]);

  // Wire an enriched item list to a fresh UploadQueue and drive the phase
  // transitions off its events. Shared by the initial upload and by
  // retryFailed() so both paths reconcile failedCount the same way. `isRetry`
  // tags the completion event so a funnel can exclude retry re-completions:
  // a batch that goes partial → retry → complete fires photo_upload_completed
  // twice, and only the first (is_retry:false) corresponds to a real batch.
  const runQueue = useCallback((items, { isRetry = false } = {}) => {
    const user = auth.currentUser;
    if (!user) {
      dispatch({ type: 'error', error: new Error('Not signed in') });
      trackEvent('photo_upload_error', { project_id: projectId, phase: 'start', error: 'Not signed in' });
      return false;
    }
    const uploadItem = buildUploader({ uid: user.uid, projectId });
    const queue = createQueue({ items, uploadItem });
    queueRef.current = queue;
    beginBatch(projectId, { count: items.length });
    dispatch({ type: 'startUploading' });
    const unsubscribe = queue.subscribe((evt) => {
      if (evt.type === 'progress' || evt.type === 'itemDone' || evt.type === 'itemFailed') {
        dispatch({ type: 'progress', aggregate: evt.aggregate || queue.getAggregate(), items: queue.getItems() });
      }
      if (evt.type === 'done') {
        completeBatch(projectId);
        const agg = evt.aggregate || queue.getAggregate();
        const cancelled = queue.state === 'cancelled';
        const failedCount = agg?.failedCount || 0;
        // failedCount > 0 means photos silently dropped — do NOT report success.
        const finalPhase = cancelled ? 'cancelled' : (failedCount > 0 ? 'partial' : 'done');
        if (cancelled) {
          trackEvent('photo_upload_cancelled', {
            project_id: projectId,
            uploaded_count: agg?.doneCount || 0,
            remaining_count: Math.max(0, (agg?.totalItems || 0) - (agg?.doneCount || 0)),
          });
        } else {
          trackEvent('photo_upload_completed', {
            project_id: projectId,
            photo_count: agg?.totalItems || 0,
            uploaded_count: agg?.doneCount || 0,
            failed_count: failedCount,
            outcome: failedCount > 0 ? 'partial' : 'complete',
            is_retry: isRetry,
            count_bucket: photoCountBucket(agg?.totalItems || 0),
            total_mb: toMb(agg?.totalBytes),
          });
        }
        const finalItems = queue.getItems();
        dispatch({ type: 'finish', phase: finalPhase, aggregate: agg, items: finalItems });
        unsubscribe();
        // A fully-successful client upload can still hide a server-side drop —
        // verify the uploaded keys landed as docs and recover any that didn't.
        if (finalPhase === 'done') {
          const uploadedKeys = finalItems
            .filter((it) => it.status === 'done')
            .map((it) => it.photoKey);
          verifyAndReconcile(uploadedKeys);
        }
      }
    });
    queue.start();
    return true;
  }, [buildUploader, createQueue, projectId, verifyAndReconcile]);

  const preflight = useCallback(async (files) => {
    dispatch({ type: 'scanStart' });
    capHitFiredRef.current = false;
    try {
      const scan = await runScan(files);
      const existingIds = await fetchExistingIds(projectId);
      const dedup = diffAgainstExisting(scan.entries, existingIds);
      const caps = evaluateCaps({
        existingPhotos,
        existingBytes,
        batchPhotos: dedup.totals.toUploadCount,
        batchBytes: dedup.totals.toUploadBytes,
        poolTotalBytes,
        poolUsedBytes,
        poolLoading,
      });
      dispatch({ type: 'scanDone', scan, dedup, caps });
      trackEvent('photo_preflight_scanned', {
        project_id: projectId,
        photo_count: dedup.totals.toUploadCount,
        count_bucket: photoCountBucket(dedup.totals.toUploadCount),
        scanned_count: scan.totals.photoCount,
        already_uploaded_count: dedup.totals.alreadyUploadedCount,
        day_count: scan.totals.dayCount,
        missing_exif: scan.missingExif,
        skipped_non_images: scan.skippedNonImages.length,
        total_mb: toMb(dedup.totals.toUploadBytes),
        blocked: !!caps.blocked,
      });
      emitCapHit(caps, dedup.totals);
    } catch (err) {
      dispatch({ type: 'error', error: err });
      trackEvent('photo_upload_error', {
        project_id: projectId,
        phase: 'preflight',
        error: (err && err.message) || String(err),
      });
    }
  }, [projectId, runScan, fetchExistingIds, existingPhotos, existingBytes, poolTotalBytes, poolUsedBytes, poolLoading, emitCapHit]);

  // caps is computed once at preflight from the pooled inputs captured at that
  // instant. The account rollup / tier entitlement resolve asynchronously, so a
  // preflight opened during the poolLoading window freezes caps at
  // poolPending/blocked and would stay stuck on "calculating…" with Start
  // disabled — preflight only re-runs on a user drop. Re-verdict caps whenever a
  // pooled input changes while a preflight is open, from the same stored batch,
  // so the dialog unblocks the moment real usage/allowance is known (#362).
  useEffect(() => {
    if (state.phase !== 'preflight' || !state.dedup) return;
    const caps = evaluateCaps({
      existingPhotos,
      existingBytes,
      batchPhotos: state.dedup.totals.toUploadCount,
      batchBytes: state.dedup.totals.toUploadBytes,
      poolTotalBytes,
      poolUsedBytes,
      poolLoading,
    });
    dispatch({ type: 'capsUpdate', caps });
    // A preflight opened during the poolLoading window is blocked with no real
    // over-cap verdict yet, so the scan-time emit deliberately stayed quiet.
    // Report it here once the pooled usage resolves into a genuine overage;
    // emitCapHit's latch keeps it to one event for the scan either way.
    emitCapHit(caps, state.dedup.totals);
    // Deps are exactly the evaluateCaps inputs that can change post-preflight;
    // state.caps is deliberately absent (capsUpdate writes it — depending on it
    // would loop), so we re-verdict only on pooled-input / batch changes.
  }, [poolTotalBytes, poolUsedBytes, poolLoading, existingPhotos, existingBytes, state.phase, state.dedup, emitCapHit]);

  const startUpload = useCallback(() => {
    if (!state.dedup || state.dedup.toUpload.length === 0) return;
    if (state.caps?.blocked) return;
    // Cameras are opt-in — omit unless the caller supplies one.
    const enriched = state.dedup.toUpload.map((e) => ({ ...e, cameraId: cameraId || null }));
    batchItemsRef.current = enriched;
    if (!runQueue(enriched)) return;
    trackEvent('photo_upload_started', {
      project_id: projectId,
      photo_count: enriched.length,
      count_bucket: photoCountBucket(enriched.length),
      total_mb: toMb(state.dedup.totals.toUploadBytes),
    });
  }, [state.dedup, state.caps, cameraId, runQueue, projectId]);

  // Re-queue only the items that failed in the last run. Lets the user recover
  // from transient upload failures instead of being told the batch is done.
  const retryFailed = useCallback(() => {
    const failedKeys = new Set(
      (queueRef.current?.getItems() || [])
        .filter((it) => it.status === 'failed')
        .map((it) => it.photoKey),
    );
    if (failedKeys.size === 0) return;
    const retryItems = batchItemsRef.current.filter((e) => failedKeys.has(e.photoKey));
    if (retryItems.length === 0) return;
    // Narrow the batch to the retry set so further retries operate on it.
    batchItemsRef.current = retryItems;
    trackEvent('photo_upload_retry_failed', {
      project_id: projectId,
      retry_count: retryItems.length,
      count_bucket: photoCountBucket(retryItems.length),
    });
    runQueue(retryItems, { isRetry: true });
  }, [runQueue, projectId]);

  const cancelUpload = useCallback(() => {
    if (queueRef.current) queueRef.current.cancel();
  }, []);

  const reset = useCallback(() => {
    if (queueRef.current) queueRef.current.cancel();
    queueRef.current = null;
    dispatch({ type: 'reset' });
  }, []);

  // On unmount cancel any in-flight uploads so a route change doesn't leak
  // the queue's timers / abort handlers, and flag the reconcile guard so a
  // late-resolving verify doesn't dispatch into an unmounted hook.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (queueRef.current) queueRef.current.cancel();
    };
  }, []);

  const dismissReconcile = useCallback(() => {
    dispatch({ type: 'dismissReconcile' });
  }, []);

  return {
    phase: state.phase,
    scan: state.scan,
    dedup: state.dedup,
    caps: state.caps,
    progress: state.progress,
    itemStates: state.itemStates,
    error: state.error,
    reconcile: state.reconcile,
    preflight,
    startUpload,
    retryFailed,
    cancelUpload,
    reset,
    dismissReconcile,
  };
}
